import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { isEmpty, isNull, orderBy } from 'lodash';
import * as roomActions from 'features/room-builder/duck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as taskActions from 'features/task-management/duck';
import * as onlineTestingActions from '../duck';
import {
  ONLINE_PROVIDERS,
  ONLINE_REGS_STATUSES,
  ONLINE_REGS_STATUS_INITIALS,
  ROOM_STATUS,
  WKYS_EXAMINEE_TYPES,
} from 'shared/constants';
import StaticService from 'shared/services/StaticService';
import { getUpcomingTasks } from 'features/task-management/selectors';
import { RoomPolling } from 'features/task-management';
import {
  Tabs,
  Tab,
  Spinner,
  AlertNotice,
  RefreshButton,
  AmountOfTotal,
  ScrollFade,
  CustomFilters,
  CheckboxList,
} from 'shared/components';
import {
  GridView,
  ListView,
  StudentGridCell,
  AuthorizationTickets,
  LockBar,
  TaskSection,
  SealCodes,
  BulkChangeStatusModal,
} from '../index';
import { mapOnlineRegs } from '../selectors';
import NationalOnlineTestingDashboard from './NationalOnlineTestingDashboard';
import { useNavigate } from 'react-router-dom';

import './OnlineTestingDashboard.scss';

const TABS = {
  listView: 1,
  gridView: 2,
};

const ASSESSMENT_TABS = Object.entries(WKYS_EXAMINEE_TYPES)
  .filter(([code]) => code !== 'ncrc')
  .map(([code, title], index) => ({
    id: index + 1,
    code,
    title,
  }));

// Get reg type ID for the selected NCRC assessment tab
const getRegTypeIdForTab = (tabId, regTypes) => {
  const assessment = ASSESSMENT_TABS[tabId - 1];
  return assessment && regTypes.length
    ? regTypes.find((rt) => rt.code === assessment.code.toUpperCase())?.id || null
    : null;
};

const getCheckboxList = () => {
  const checkboxList = [];
  Object.entries(ONLINE_REGS_STATUSES).forEach(([key, value], i) => {
    if (i > 0) {
      checkboxList.push({
        value: key,
        label: value,
        checked: false,
        id: i,
        key: i,
      });
    }
  });
  return checkboxList;
};

const isCheckboxAvailable = (reg) =>
  reg.testStatus === ONLINE_REGS_STATUS_INITIALS.active ||
  reg.testStatus === ONLINE_REGS_STATUS_INITIALS.resumed ||
  reg.testStatus === ONLINE_REGS_STATUS_INITIALS.exited;

const OnlineTestingDashboard = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  let poller;
  const checkboxList = getCheckboxList();
  const { roomId, eventId } = props.match.params;
  const [seatedExaminees, setSeatedExaminees] = useState([]);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [selectedTab, setSelectedTab] = useState(TABS.listView);
  const [selectedAssessmentTab, setSelectedAssessmentTab] = useState(1);
  const [filterCheckboxes, setFilterCheckboxes] = useState(checkboxList);
  const [listOfSelectedExaminees, setListOfSelectedExaminees] = useState([]);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [tableScrollPosition, setTableScrollPosition] = useState(0);
  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false);
  const [regTypes, setRegTypes] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    if (sessionStorage.getItem('onlineTestingRedirect')) {
      sessionStorage.removeItem('onlineTestingRedirect');
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    Array.from(document.querySelectorAll('.btn-change-status')).forEach((el) => {
      const popover = bootstrap.Popover.getOrCreateInstance(el);
      if (listOfSelectedExaminees.length) {
        popover.disable();
      } else {
        popover.enable();
      }
    });
  }, [listOfSelectedExaminees]);

  const {
    room,
    onlineRegs,
    examineesMappedToSeats,
    loadingRegs,
    upcomingTasks,
    tcEvent,
    roomStatus,
  } = useSelector(
    (state) => ({
      room: state.roomOptions.room,
      onlineRegs: state.onlineTesting.onlineRegs,
      examineesMappedToSeats: mapOnlineRegs(state),
      loadingRegs: state.onlineTesting.loadingRegs,
      upcomingTasks: getUpcomingTasks(state),
      tcEvent: state.tcEvent.currentEvent,
      roomStatus: state.taskManagement.roomStatus,
    }),
    shallowEqual
  );
  const dispatch = useDispatch();

  const allStudentsNotReady = onlineRegs.every((reg) => reg.testStatus.length === 0);

  const isNCRCRoom = useMemo(() => room?.roomType?.code?.toLowerCase().includes('ncrc'), [room]);

  const isPsiEvent = useMemo(
    () =>
      !isEmpty(tcEvent) && tcEvent.testingEvent?.adminId?.onlineProvider === ONLINE_PROVIDERS.psi,
    [tcEvent]
  );

  const noFilterChecked = useMemo(
    () => filterCheckboxes.every((box) => !box.checked),
    [filterCheckboxes]
  );

  const filteredRegs = useMemo(
    () =>
      noFilterChecked
        ? orderBy(onlineRegs, 'lastName')
        : orderBy(
            filterCheckboxes
              .filter((box) => box.checked)
              .flatMap((box) => onlineRegs.filter((reg) => box.value === reg.testStatus)),
            'lastName'
          ),
    [filterCheckboxes, noFilterChecked, onlineRegs]
  );

  useEffect(() => {
    if (loadingRegs || !onlineRegs.length) return;
    setSeatedExaminees(onlineRegs.filter((ex) => !isNull(ex.seatCol) && !isNull(ex.seatRow)));
    setListOfSelectedExaminees((prev) =>
      onlineRegs.filter(
        (reg) => isCheckboxAvailable(reg) && prev.some((selected) => selected.id === reg.id)
      )
    );
  }, [loadingRegs, onlineRegs]);

  useEffect(() => {
    if (listOfSelectedExaminees.some((reg) => !isCheckboxAvailable(reg))) {
      setListOfSelectedExaminees(listOfSelectedExaminees.filter((reg) => isCheckboxAvailable(reg)));
    }
  }, [filteredRegs, listOfSelectedExaminees]);

  // Fetch online regs; NCRC rooms must fetch regTypes first
  useEffect(() => {
    // We don't know if the room is NCRC until we have the room data
    if (isPsiEvent || isEmpty(room) || room.loading || isEmpty(tcEvent)) return;
    if (isNCRCRoom) {
      const cycleId = tcEvent.testingEvent.adminId.cycleId.id;
      StaticService.getRegTypes(cycleId).then((response) => {
        const rts = response.data || [];
        const regTypeId = getRegTypeIdForTab(selectedAssessmentTab, rts);
        setRegTypes(rts);
        dispatch(onlineTestingActions.getOnlineRegs(roomId, regTypeId));
      });
    } else {
      dispatch(onlineTestingActions.getOnlineRegs(roomId));
    }
  }, [dispatch, isNCRCRoom, isPsiEvent, room, roomId, selectedAssessmentTab, tcEvent]);

  useEffect(() => {
    dispatch(roomActions.getRoom(roomId));
    dispatch(taskActions.getRoomStatus(roomId));
    dispatch(taskActions.getRoomTimers(roomId));

    if (isEmpty(tcEvent)) {
      dispatch(tcEventActions.getTcEvent(eventId)).then((data) => {
        if (!isEmpty(data)) {
          const cycleId = data.testingEvent.adminId.cycleId.id;
          dispatch(taskActions.getTasks(cycleId));
        }
      });
    } else {
      const cycleId = tcEvent.testingEvent.adminId.cycleId.id;
      dispatch(taskActions.getTasks(cycleId));
    }

    // Only set up polling for non-PSI events, as NationalOnlineTestingDashboard handles its own refresh
    if (!poller && !isPsiEvent) {
      poller = new RoomPolling({
        action: () => dispatch(taskActions.getRoomStatus(roomId)),
      });
    }

    return () => {
      dispatch(onlineTestingActions.getOnlineRegsAction({ data: [] }));
      if (poller) {
        poller.stop();
        poller = null;
      }
    };
  }, [isPsiEvent]);

  const clearCheckboxes = useCallback(() => {
    setFilterCheckboxes((prev) => prev.map((box) => ({ ...box, checked: false })));
  }, []);

  const fetchData = useCallback(
    (activeTab) => {
      const regTypeId = isNCRCRoom ? getRegTypeIdForTab(selectedAssessmentTab, regTypes) : null;
      dispatch(onlineTestingActions.getOnlineRegs(roomId, regTypeId));

      if (activeTab) {
        setSelectedTab(activeTab);
        if (activeTab === TABS.gridView) {
          dispatch(roomActions.getRoom(roomId));
        }
      } else if (selectedTab === TABS.gridView) {
        dispatch(roomActions.getRoom(roomId));
      }
      setLastFetchTime(moment().format('hh:mm A'));
    },
    [dispatch, isNCRCRoom, roomId, selectedAssessmentTab, regTypes, selectedTab]
  );

  const onCheck = useCallback((e) => {
    setFilterCheckboxes((prev) =>
      prev.map((box) => ({
        ...box,
        checked: box.id === Number(e.target.name) ? e.target.checked : box.checked,
      }))
    );
  }, []);

  const onClear = useCallback(() => {
    clearCheckboxes();
    setScrollPosition(0);
  }, [clearCheckboxes]);

  const updateSelectedExamineesList = useCallback((selectedList) => {
    setListOfSelectedExaminees(selectedList);
  }, []);

  const goBackToPrevPage = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const toggleStatusModal = useCallback((clearList = false) => {
    setChangeStatusModalOpen((prev) => !prev);
    if (clearList) {
      setListOfSelectedExaminees([]);
    }
  }, []);

  const renderTabContent = (
    onlineRegs,
    filteredRegs,
    roomStatus,
    isSelectionAvailable,
    listOfSelectedExaminees,
    toggleStatusModal,
    onClear,
    updateSelectedExamineesList,
    tableScrollPosition,
    setTableScrollPosition,
    tcEvent,
    roomId
  ) =>
    onlineRegs.length > 0 ? (
      <>
        {(roomStatus.status === ROOM_STATUS.opened || isSelectionAvailable) && (
          <div
            className={`border-box-radius details-bar ${
              isSelectionAvailable ? 'small-padding' : ''
            }`}>
            {isSelectionAvailable && (
              <div className="change-status-section">
                <AmountOfTotal
                  label={t('onlineTesting.listView.selectedLabel')}
                  total={filteredRegs.length}
                  nrOfTotal={
                    listOfSelectedExaminees.filter((sel) =>
                      filteredRegs.find((reg) => reg.id === sel.id)
                    ).length
                  }
                />
                <span
                  className="btn-change-status"
                  data-bs-toggle="popover"
                  data-bs-placement="top"
                  data-bs-trigger="hover"
                  data-bs-content={t('onlineTesting.listView.changeStatusTooltip')}>
                  <button
                    className="btn btn-primary btn-small"
                    onClick={() => toggleStatusModal()}
                    disabled={!listOfSelectedExaminees.length}>
                    {t('onlineTesting.listView.changeStatusLabel')}
                  </button>
                </span>
              </div>
            )}
            {roomStatus.status === ROOM_STATUS.opened && (
              <LockBar
                isNCRCRoom={isNCRCRoom}
                onlineRegs={filteredRegs}
                regTypes={regTypes}
                roomId={roomId}
                selectedAssessmentTab={selectedAssessmentTab}
              />
            )}
          </div>
        )}

        {filteredRegs.length > 0 ? (
          <ListView
            rowHeaders={['name', 'seat', 'status']}
            onlineRegs={filteredRegs}
            listOfSelectedExaminees={listOfSelectedExaminees}
            updateExamineesList={updateSelectedExamineesList}
            scrollPosition={tableScrollPosition}
            setScrollPosition={setTableScrollPosition}
            seatingChartRequired={tcEvent?.testingEvent?.adminId?.cycleId?.seatingChartRequiredFlag}
          />
        ) : (
          <div>
            <div className="no-results">{t('onlineTesting.listView.noResults')}</div>
            <button className="btn btn-outline btn-small no-results-button" onClick={onClear}>
              {t('onlineTesting.listView.clearFilters')}
            </button>
          </div>
        )}
      </>
    ) : (
      <AlertNotice message={t('onlineTesting.listView.emptyStateMessage')} />
    );

  const getLeftSection = () => (
    <div className="left-section">
      {windowSize > 1199 && (
        <>
          <h4>{t('onlineTesting.title')}</h4>
          <div className="fw-bold padding-b-12">{room.name}</div>
        </>
      )}
      {roomStatus.status === ROOM_STATUS.opened && (
        <div className="task-timer-section">
          <TaskSection upcomingTasks={upcomingTasks} roomId={roomId} taskActions={taskActions} />
          {!upcomingTasks.length && (
            <AlertNotice
              message={t('onlineTesting.tasks.infoMessage')}
              onClick={() => navigate(`/test-event/${eventId}/room/${roomId}`)}
            />
          )}
        </div>
      )}
      <SealCodes roomId={roomId} roomStatus={roomStatus.status} isNCRCRoom={isNCRCRoom} />
      {selectedTab === TABS.listView && onlineRegs.length > 0 && !allStudentsNotReady && (
        <CustomFilters
          title={t('onlineTesting.listView.filters')}
          clear={noFilterChecked ? '' : t('onlineTesting.listView.clear')}
          onClear={onClear}
          subTitle={t('onlineTesting.listView.show')}>
          <ScrollFade
            setScrollPosition={setScrollPosition}
            scrollPosition={scrollPosition}
            filterCheckboxes={filterCheckboxes}>
            <CheckboxList onChange={onCheck} checkboxes={filterCheckboxes} />
          </ScrollFade>
        </CustomFilters>
      )}
      {windowSize > 1199 && (
        <button className="btn btn-outline btn-small m-0 w-100" onClick={goBackToPrevPage}>
          {t('common.labels.back')}
        </button>
      )}
    </div>
  );
  const isSelectionAvailable = onlineRegs.some((reg) => isCheckboxAvailable(reg));

  if (isEmpty(room) || (room.loading && room.id !== +roomId)) {
    return <Spinner />;
  }

  if (isPsiEvent) {
    return <NationalOnlineTestingDashboard />;
  }

  return (
    <div className="online-testing-container">
      {windowSize <= 1199 && (
        <>
          <h4>{t('onlineTesting.title')}</h4>
          <div className="fw-bold padding-b-12">{room.name}</div>
        </>
      )}
      {windowSize > 1199 && getLeftSection()}
      <div className="right-section">
        <div>
          <div>
            {isNCRCRoom ? (
              <div className="assessment-tabs">
                <Tabs
                  defaultTab={1}
                  onClickAction={(tabIndex) => {
                    setSelectedAssessmentTab(tabIndex);
                    setListOfSelectedExaminees([]);
                    clearCheckboxes();
                  }}
                  loadingData={loadingRegs}
                  rightElement={
                    <div className="d-flex refresh">
                      <RefreshButton fetchData={fetchData} lastFetchTime={lastFetchTime} />
                      {onlineRegs.length > 0 && (
                        <AuthorizationTickets
                          listOfExaminees={onlineRegs}
                          testCenterEventId={tcEvent?.id}
                        />
                      )}
                    </div>
                  }
                  extraContent={windowSize <= 1199 && getLeftSection()}>
                  {ASSESSMENT_TABS.map((assessmentTab) => (
                    <Tab key={assessmentTab.id} title={assessmentTab.title}>
                      {renderTabContent(
                        onlineRegs,
                        filteredRegs,
                        roomStatus,
                        isSelectionAvailable,
                        listOfSelectedExaminees,
                        toggleStatusModal,
                        onClear,
                        updateSelectedExamineesList,
                        tableScrollPosition,
                        setTableScrollPosition,
                        tcEvent,
                        roomId
                      )}
                    </Tab>
                  ))}
                </Tabs>
              </div>
            ) : (
              <Tabs
                defaultTab={TABS.listView}
                onClickAction={fetchData}
                loadingData={loadingRegs}
                rightElement={
                  <div className="d-flex refresh">
                    <RefreshButton fetchData={fetchData} lastFetchTime={lastFetchTime} />
                    {onlineRegs.length > 0 && (
                      <AuthorizationTickets
                        listOfExaminees={onlineRegs}
                        testCenterEventId={tcEvent?.id}
                      />
                    )}
                  </div>
                }
                extraContent={windowSize <= 1199 && getLeftSection()}>
                <Tab title={t('onlineTesting.tabs.listView')}>
                  {onlineRegs.length > 0 ? (
                    <>
                      {(roomStatus.status === ROOM_STATUS.opened || isSelectionAvailable) && (
                        <div
                          className={`border-box-radius details-bar ${
                            isSelectionAvailable ? 'small-padding' : ''
                          }`}>
                          {isSelectionAvailable && (
                            <div className="change-status-section">
                              <AmountOfTotal
                                label={t('onlineTesting.listView.selectedLabel')}
                                total={onlineRegs.length}
                                nrOfTotal={listOfSelectedExaminees.length}
                              />
                              <span
                                className="btn-change-status"
                                data-bs-toggle="popover"
                                data-bs-placement="top"
                                data-bs-trigger="hover"
                                data-bs-content={t('onlineTesting.listView.changeStatusTooltip')}>
                                <button
                                  className="btn btn-primary btn-small "
                                  onClick={() => toggleStatusModal()}
                                  disabled={!listOfSelectedExaminees.length}>
                                  {t('onlineTesting.listView.changeStatusLabel')}
                                </button>
                              </span>
                            </div>
                          )}
                          {roomStatus.status === ROOM_STATUS.opened && (
                            <LockBar onlineRegs={onlineRegs} roomId={roomId} />
                          )}
                        </div>
                      )}

                      {filteredRegs.length > 0 ? (
                        <ListView
                          rowHeaders={['name', 'seat', 'status']}
                          onlineRegs={filteredRegs}
                          listOfSelectedExaminees={listOfSelectedExaminees}
                          updateExamineesList={updateSelectedExamineesList}
                          scrollPosition={tableScrollPosition}
                          setScrollPosition={setTableScrollPosition}
                          seatingChartRequired={
                            tcEvent?.testingEvent?.adminId?.cycleId?.seatingChartRequiredFlag
                          }
                        />
                      ) : (
                        <div>
                          <div className="no-results">{t('onlineTesting.listView.noResults')}</div>
                          <button
                            className="btn btn-outline btn-small no-results-button"
                            onClick={onClear}>
                            {t('onlineTesting.listView.clearFilters')}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <AlertNotice message={t('onlineTesting.listView.emptyStateMessage')} />
                  )}
                </Tab>
                {tcEvent?.testingEvent?.adminId?.cycleId?.seatingChartRequiredFlag && (
                  <Tab title={t('onlineTesting.tabs.gridView')}>
                    {seatedExaminees.length ? (
                      <React.Fragment>
                        <div className="border-box-radius details-bar grid-details">
                          <AmountOfTotal
                            label={t('onlineTesting.gridView.seatedLabel')}
                            total={onlineRegs.length}
                            nrOfTotal={seatedExaminees.length}
                          />
                          {roomStatus.status === ROOM_STATUS.opened && (
                            <LockBar onlineRegs={onlineRegs} roomId={roomId} />
                          )}
                        </div>
                        <GridView
                          rows={room.seatingRowCount}
                          cols={room.seatingColumnCount}
                          cellComponent={StudentGridCell}
                          seatsData={examineesMappedToSeats}
                          windowSize={windowSize}
                        />
                      </React.Fragment>
                    ) : (
                      <AlertNotice
                        message={t('onlineTesting.gridView.emptyState')}
                        link={`/test-event/${eventId}/room/${roomId}/seating-chart`}
                        withLink
                      />
                    )}
                  </Tab>
                )}
              </Tabs>
            )}
          </div>
        </div>
      </div>
      {windowSize <= 1199 && (
        <div className="button-container">
          <button className="btn btn-outline btn-small m-0 w-100" onClick={goBackToPrevPage}>
            {t('common.labels.back')}
          </button>
        </div>
      )}
      <BulkChangeStatusModal
        listOfExaminees={listOfSelectedExaminees}
        isNCRCRoom={isNCRCRoom}
        isOpen={changeStatusModalOpen}
        handleClose={toggleStatusModal}
        scrollPosition={tableScrollPosition}
        setScrollPosition={setTableScrollPosition}
        selectedAssessmentTab={selectedAssessmentTab}
        regTypes={regTypes}
        roomId={roomId}
      />
    </div>
  );
};

export default OnlineTestingDashboard;

OnlineTestingDashboard.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      roomId: PropTypes.string.isRequired,
      eventId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};
